import { useCallback, useState } from 'react';
import { DEFAULT_THEME, THEME_NAMES, type ThemeName } from '../types.js';
import { THEME_STORAGE_KEY } from '../themeBootstrap.js';

export { THEME_STORAGE_KEY };

function isValidTheme(value: string | null): value is ThemeName {
  return typeof value === 'string' && (THEME_NAMES as readonly string[]).includes(value);
}

function readInitialTheme(): ThemeName {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (isValidTheme(stored)) return stored;
  } catch {
    /* ignore */
  }
  if (typeof document !== 'undefined') {
    const fromDom = document.documentElement.getAttribute('data-theme');
    if (isValidTheme(fromDom)) return fromDom;
  }
  return DEFAULT_THEME;
}

function persistTheme(theme: ThemeName): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* ignore */
  }
}

function applyTheme(theme: ThemeName): void {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', theme);
  }
}

export interface UseThemeResult {
  theme: ThemeName;
  setTheme: (next: ThemeName) => void;
}

export function useTheme(): UseThemeResult {
  const [theme, setThemeState] = useState<ThemeName>(() => readInitialTheme());

  const setTheme = useCallback((next: ThemeName) => {
    applyTheme(next);
    persistTheme(next);
    setThemeState(next);
  }, []);

  return { theme, setTheme };
}